import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Image as ImageIcon, ZoomIn } from 'lucide-react';
import { galeriaAPI } from '@/lib/api';

export default function Galeria() {
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFoto, setSelectedFoto] = useState(null);

  useEffect(() => {
    const fetchFotos = async () => {
      try {
        const res = await galeriaAPI.listar();
        setFotos(res.data);
      } catch (error) {
        console.error('Erro ao carregar fotos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFotos();
  }, []);

  const getImageUrl = (foto) => {
    if (foto.url.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${foto.url}`;
    }
    return foto.url;
  };

  // Fotos reais da ACEC Feminino
  const placeholderImages = [
    { id: '1', url: 'https://customer-assets.emergentagent.com/job_c5c7b024-2dc8-4b4b-80a1-e26f6491db2a/artifacts/pcuf1s0w_WhatsApp%20Image%202026-03-05%20at%2008.26.28.jpeg', titulo: 'Time Completo ACEC' },
    { id: '2', url: 'https://customer-assets.emergentagent.com/job_c5c7b024-2dc8-4b4b-80a1-e26f6491db2a/artifacts/ai0vmgs1_WhatsApp%20Image%202026-03-05%20at%2008.26.12.jpeg', titulo: 'Treino das Atletas' },
    { id: '3', url: 'https://customer-assets.emergentagent.com/job_c5c7b024-2dc8-4b4b-80a1-e26f6491db2a/artifacts/v1dcyyt3_WhatsApp%20Image%202026-03-05%20at%2008.49.11.jpeg', titulo: 'Equipe com as Bolas Mikasa' },
    { id: '4', url: 'https://customer-assets.emergentagent.com/job_c5c7b024-2dc8-4b4b-80a1-e26f6491db2a/artifacts/r1ne1dy1_WhatsApp%20Image%202026-03-05%20at%2008.24.59%20%281%29.jpeg', titulo: 'Aquecimento na Quadra' },
  ];

  const displayFotos = fotos.length > 0 ? fotos : placeholderImages;

  return (
    <div data-testid="galeria-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-blue-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 hero-pattern" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <ImageIcon className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase">
              <span className="text-yellow-400">Galeria</span> de Fotos
            </h1>
            <p className="text-xl text-blue-100 mt-4 max-w-2xl mx-auto">
              Momentos especiais das nossas atletas
            </p>
          </motion.div>
        </div>
      </section>

      {/* Galeria */}
      <section className="py-16 bg-slate-50" data-testid="galeria-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : displayFotos.length === 0 ? (
            <div className="text-center py-20">
              <ImageIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="font-heading text-2xl text-slate-600 mb-2">
                Nenhuma foto ainda
              </h3>
              <p className="text-slate-500">
                As fotos serão adicionadas em breve.
              </p>
            </div>
          ) : (
            <div className="masonry-grid">
              {displayFotos.map((foto, index) => (
                <motion.div
                  key={foto.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="masonry-item group relative cursor-pointer"
                  onClick={() => setSelectedFoto(foto)}
                  data-testid={`galeria-item-${foto.id}`}
                >
                  <div className="relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                    <img
                      src={foto.url.startsWith('/api') ? getImageUrl(foto) : foto.url}
                      alt={foto.titulo || 'Foto ACEC'}
                      className="w-full object-cover"
                      style={{ minHeight: '200px' }}
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-blue-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <ZoomIn className="w-10 h-10 text-white" />
                    </div>

                    {/* Caption */}
                    {foto.titulo && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-4">
                        <p className="text-white font-medium text-sm">
                          {foto.titulo}
                        </p>
                        {foto.descricao && (
                          <p className="text-white/70 text-xs mt-1 line-clamp-2">
                            {foto.descricao}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedFoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lightbox-overlay flex items-center justify-center p-4"
            onClick={() => setSelectedFoto(null)}
            data-testid="lightbox-overlay"
          >
            <button
              className="absolute top-6 right-6 text-white hover:text-yellow-400 transition-colors"
              onClick={() => setSelectedFoto(null)}
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-5xl max-h-[90vh] relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedFoto.url.startsWith('/api') ? getImageUrl(selectedFoto) : selectedFoto.url}
                alt={selectedFoto.titulo || 'Foto ACEC'}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
              {selectedFoto.titulo && (
                <div className="text-center mt-4">
                  <p className="text-white font-heading text-xl">
                    {selectedFoto.titulo}
                  </p>
                  {selectedFoto.descricao && (
                    <p className="text-slate-300 mt-2">
                      {selectedFoto.descricao}
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
